import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Input,
  Button,
  Stack,
  Title,
  Datagrid,
  Badge,
  Menu,
  Divider,
  Text,
  Select,
  Skeleton,
  DEFAULT_INPUT_HEIGHT
} from '@efast_public/fjd-component-library';
import { useMutation } from '@tanstack/react-query';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import {
  ConfiguredOnlineService,
  GovOsResult,
  PaginationRequest,
  UpdateVerfahrenStateRequest
} from '../api/govos.types';
import { RouteKey } from './AppRoutes';
import { debounce, DEFAULT_DEBOUNCE_DELAY } from '../utils/debounce';
import { TableMenuButton } from '../shared/components/TableMenuButton';
import { useNotification } from '../shared/notification/useNotification';
import { useConfiguredOnlineDienstFetcher } from '../api/hooks/useConfiguredOnlineDienstFetcher';
import { createOnlinedienstTemplateUrl } from '../utils/odUtil';
import { usePartnerFetcher } from '../api/hooks/usePartnerFetcher';
import { SelectOption } from '@efast_public/fjd-component-library/build/src/common/components/BaseSelect/BaseSelect.types';

interface SearchFilter {
  search: string;
  startApp: string[];
}

const PAGE_SIZE = 10;
const ROW_HEIGHT = 76;
const HEADER_HEIGHT = 46;
const TABLE_HEIGHT = PAGE_SIZE * ROW_HEIGHT + HEADER_HEIGHT;
const SESSION_STORAGE_KEY = 'odm-filter-state';

export function ManagedOnlinedienstePage() {
  const { t } = useTranslation();
  const { notifyUser, confirmByUser } = useNotification();

  const navigate = useNavigate();
  const { notifyUserFromRequest } = useNotification();
  // Load filter state from sessionStorage on mount
  const getInitialState = () => {
    try {
      const savedState = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        return {
          search: parsedState.search || '',
          appIds: parsedState.appIds || []
        };
      }
    } catch (error) {
      console.error('Failed to parse saved filter state:', error);
    }
    return { search: '', appIds: [] };
  };

  const initialState = getInitialState();
  const [pagination, setPagination] = useState<
    PaginationRequest<{ appIds: number[] }>
  >({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
    search: initialState.search,
    params: {
      appIds: initialState.appIds
    }
  });
  const [searchInput, setSearchInput] = useState(initialState.search);
  const isMounted = useRef(true);

  const { data: partners } = usePartnerFetcher();

  const { onlinedienstResponse, onlinedienstStatusResponse } =
    useConfiguredOnlineDienstFetcher(pagination);

  const { mutate: deleteOnlineDienst, isPending: isDeleting } = useMutation({
    mutationFn: async ({ processId }: { processId: number }) =>
      notifyUserFromRequest(
        () =>
          govosClient.delete<GovOsResult<unknown>>(
            GovosApiRoutes.configuredOnlineDienst(processId)
          ),
        {
          successMessage: t('ODOverviewPage.deleteSuccess'),
          errorMessage: t('ODOverviewPage.deleteError'),
          loadingMessage: t('ODOverviewPage.deleteLoading')
        }
      ),
    onSuccess: () => {
      onlinedienstStatusResponse.refetch();
      onlinedienstResponse.refetch();
      setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
    }
  });

  const { mutate: deactivateOnlineDienst, isPending: isDeactivating } =
    useMutation({
      mutationFn: async ({ processId }: { processId: number }) =>
        notifyUserFromRequest(
          () =>
            govosClient.put<GovOsResult<unknown>>(
              GovosApiRoutes.onlineServiceState(processId),
              { shouldDeactivate: true } as UpdateVerfahrenStateRequest
            ),
          {
            successMessage: t('ManagedOnlinedienstDetail.deactivateSuccess'),
            errorMessage: t('ManagedOnlinedienstDetail.deactivateError'),
            loadingMessage: t('ManagedOnlinedienstDetail.deactivateLoading')
          }
        ),
      onSuccess: () => {
        onlinedienstStatusResponse.refetch();
        onlinedienstResponse.refetch();
      }
    });

  const handleSearchChange = debounce((filter: SearchFilter) => {
    const appIds =
      filter.startApp.length === 0
        ? partners?.map((p) => p.appiid)
        : filter.startApp.map((s) => Number(s));
    setPagination((p) => ({
      ...p,
      search: filter.search || '',
      params: {
        appIds
      }
    }));
  }, DEFAULT_DEBOUNCE_DELAY);

  const saveToSessionStorage = (search: string, appIds: number[]) => {
    if (!isMounted.current) return;
    const stateToSave = {
      search,
      appIds
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToSave));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    saveToSessionStorage(value, pagination.params?.appIds || []);
    handleSearchChange({
      search: value,
      startApp: pagination.params?.appIds.map(String) ?? []
    });
  };

  const handleSelectChange = (value: SelectOption[]) => {
    const appIds =
      value.length === 0
        ? partners?.map((p) => p.appiid) || []
        : value.map((v) => Number(v.value));
    saveToSessionStorage(pagination.search || '', appIds);
    handleSearchChange({
      search: pagination.search || '',
      startApp: value.map((v) => v.value)
    });
  };

  return (
    <Stack spacing="xxl" orientation="vertical">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Title level="2">{t('ODOverviewPage.title')}</Title>
        <Button
          variant="primary"
          beforeIcon="add"
          onClick={() => navigate(RouteKey.MARKETPLACE_ONLINEDIENSTE)}
        >
          {t('ODOverviewPage.addOnlineServiceButton')}
        </Button>
      </div>

      <Input
        placeholder={t('ODOverviewPage.searchBarPlaceholder')}
        value={searchInput}
        onChange={handleInputChange}
      />
      <Stack>
        <Stack>
          <div style={{ width: '20vw' }}>
            <Select
              placeholder={
                partners?.length === 0
                  ? t('select.noOptionsAvailable')
                  : t('ODOverviewPage.selectStartAppPlaceholder')
              }
              disabled={partners?.length === 0}
              onChange={handleSelectChange}
              value={pagination.params?.appIds.map(String) ?? []}
              options={
                partners.map((service) => ({
                  value: String(service.appiid),
                  subline: service.startApp.name,
                  label: service.dispname || 'Amt ' + service.appiid
                })) || []
              }
            />
          </div>
        </Stack>

        <Datagrid<ConfiguredOnlineService>
          columns={[
            {
              id: 'id',
              header: 'ID',
              accessorKey: 'processId',
              enableHiding: true,
              size: 100,
              minSize: 40,
              enableSorting: true,
              enableResizing: true,
              sortingFn: 'auto'
            },
            {
              id: 'title',
              header: 'Online-Dienst',
              accessorKey: 'title',
              enableHiding: true,
              size: 100,
              minSize: 600,
              enableSorting: true,
              enableResizing: true,
              sortingFn: 'auto',
              cell: (row) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '59px'
                  }}
                >
                  <div
                    style={{
                      display: 'block',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      maxHeight: '59px'
                    }}
                  >
                    <Text>{row.getValue<string>()}</Text>
                  </div>
                </div>
              )
            },
            {
              id: 'startappName',
              header: 'Stelle',
              accessorKey: 'startappId',
              enableHiding: true,
              size: 100,
              minSize: 40,
              enableSorting: true,
              enableResizing: true,
              sortingFn: 'auto',
              cell: (row) => (
                <Text>
                  {
                    partners.find((p) => p.appiid === row.getValue<number>())
                      ?.dispname
                  }
                </Text>
              )
            },
            {
              id: 'status',
              header: 'Status',
              accessorKey: 'isActive',
              enableHiding: true,
              size: 100,
              minSize: 40,
              enableSorting: true,
              enableResizing: true,
              sortingFn: 'auto',
              cell: (row) =>
                row.getValue<boolean>() ? (
                  <Badge variant="success">Online</Badge>
                ) : (
                  <Badge variant="warning">Offline</Badge>
                )
            },
            {
              id: 'version',
              header: 'Version',
              accessorKey: 'isNewVersionAvailable',
              enableHiding: true,
              size: 100,
              minSize: 4,
              enableSorting: false,
              enableResizing: true,
              sortingFn: 'auto',
              cell: (row) =>
                onlinedienstStatusResponse.isLoading ? (
                  <Skeleton height={DEFAULT_INPUT_HEIGHT} />
                ) : onlinedienstStatusResponse.data?.verfahrenStatus.at(
                    row.row.index
                  )?.isNewVersionAvailable ? (
                  <Badge variant="info">Update</Badge>
                ) : (
                  <Badge variant="success">Aktuell</Badge>
                )
            },
            {
              id: 'active',
              header: 'Konfiguration',
              accessorKey: 'hasConfigurationChanges',
              enableHiding: true,
              size: 100,
              minSize: 30,
              enableSorting: false,
              enableResizing: true,
              sortingFn: 'auto',
              cell: (row) =>
                onlinedienstStatusResponse.isLoading ? (
                  <Skeleton height={DEFAULT_INPUT_HEIGHT} />
                ) : onlinedienstStatusResponse.data?.verfahrenStatus.at(
                    row.row.index
                  )?.hasConfigurationChanges ? (
                  <Badge variant="warning">Entwurf</Badge>
                ) : (
                  <Badge variant="success">Aktiv</Badge>
                )
            },
            {
              id: 'menu',
              header: 'Menü',
              accessorKey: 'processId',
              enableHiding: true,
              size: 50,
              minSize: 2,
              enableSorting: false,
              enableResizing: true,
              sortingFn: 'auto',
              cell: (row) => (
                <TableMenuButton>
                  <Menu.MenuItem
                    icon="link"
                    label="Online-Dienst aufrufen"
                    disabled={!row.row.original.isActive}
                    onClick={() => {
                      window.open(
                        createOnlinedienstTemplateUrl(
                          row.row.original.processId,
                          row.row.original.startappId
                        ),
                        '_blank'
                      );
                    }}
                  />
                  <Menu.MenuItem
                    icon="copy"
                    label="Online-Dienst Url kopieren"
                    disabled={!row.row.original.isActive}
                    onClick={() => {
                      notifyUser({
                        title: t('ManagedOnlinedienstDetail.copyUrlSuccess'),
                        intent: 'success'
                      });
                      navigator.clipboard
                        .writeText(
                          createOnlinedienstTemplateUrl(
                            row.row.original.processId,
                            row.row.original.startappId
                          )
                        )
                        .catch(console.error);
                    }}
                  />
                  <Divider />
                  <Menu.MenuItem
                    icon="trashcan"
                    label={t('ODOverviewPage.deleteOnlineDienst')}
                    onClick={() => {
                      confirmByUser({
                        title: t('ManagedOnlinedienstDetail.deleteTitle'),
                        content: t('ManagedOnlinedienstDetail.deleteMessage'),
                        callback: () =>
                          deleteOnlineDienst({
                            processId: row.row.original.processId
                          })
                      });
                    }}
                    disabled={isDeleting}
                  />
                  <Menu.MenuItem
                    icon="pause"
                    label={t('ODOverviewPage.deactivateButton')}
                    disabled={isDeactivating || !row.row.original.isActive}
                    onClick={() => {
                      deactivateOnlineDienst({
                        processId: row.row.original.processId
                      });
                    }}
                  />
                </TableMenuButton>
              )
            }
          ]}
          isDataLoading={onlinedienstResponse.isLoading}
          data={onlinedienstResponse.data?.verfahren ?? []}
          labels={{
            currentPage: 'Aktuelle Seite',
            detailButton: 'Weitere Infos ein-/ausblenden',
            emptyData: 'Keine Einträge',
            nextPage: 'Nächste Seite',
            of: 'Seite {0} von {1}',
            page: 'Seite',
            pageSize: '{0} Einträge pro Seite',
            previousPage: 'Vorherige Seite',
            rows: 'Zeilen',
            tableTitle: '',
            ascBtn: 'Aufsteigend sortieren',
            descBtn: 'Absteigend sortieren',
            hideBtn: 'Spalte ausblenden'
          }}
          manualPagination
          dataKey="processId"
          manualSorting={false}
          areColumnsHideable={false}
          areColumnsResizeable
          tableHeight={TABLE_HEIGHT}
          multiSelect={false}
          onRowClick={(row) => {
            navigate(`${RouteKey.MANAGED_ONLINEDIENSTE}/${row.processId}`);
          }}
          clickableRows
          paginationState={pagination}
          onPaginationChange={(newPagination) => {
            setPagination((p) => ({
              ...p,
              ...newPagination
            }));
          }}
          totalDataCount={onlinedienstResponse.data?.pagination.totalCount}
        />
      </Stack>
    </Stack>
  );
}
